import imgCropUrl from '@/home/utils/imgCropUrl';
import TrackWrapper from '~/components/common/TrackWrapper';
import { useIsMobile } from '~/context/common/DeviceContext';
import { BffFeedCardProps } from '~/contracts/home';
import { useHandleNativePurchaseOnClick } from '~/native/home/hooks';
import { RemainingTasks, AllTasksComplete } from './VerificationTasks';
import { defaultImageHandler } from '../../utils/defaultImageHandler';
import formatEvent from '../../utils/formatEvent';
import {
  Card,
  CardButtonWhite,
  CardHeader,
  CardImageSection,
  CardInfoSection,
  TrimMileage,
  VehicleImage,
  VehicleDetails,
  YearMakeModel
} from '../OrderStatusCards.styles';

function OpdVehicleDetails({ feedItem }: BffFeedCardProps) {
  if (feedItem.content == null || feedItem.content.vehicle == null) {
    return null;
  }

  const {
    content: {
      vehicle: { make, model, trim, year, mileage = 0 }
    }
  } = feedItem;

  return (
    <VehicleDetails>
      <YearMakeModel data-qa="order-placed-year-make-model">
        {year} {make} {model}
      </YearMakeModel>
      <TrimMileage data-qa="order-placed-trim">
        {trim}
        <b>&nbsp;&nbsp;•&nbsp;&nbsp;</b>
        {mileage.toLocaleString()} miles
      </TrimMileage>
    </VehicleDetails>
  );
}

export default function OrderPlacedCard({ feedItem }: BffFeedCardProps) {
  const isMobile = useIsMobile();
  const handleNativePurchaseOnClick = useHandleNativePurchaseOnClick();

  if (
    feedItem.callToAction == null ||
    feedItem.content == null ||
    feedItem.content.vehicle == null ||
    feedItem.content.openVerifications == null
  ) {
    return null;
  }

  const {
    callToAction: { title: callToActionTitle, link: callToActionLink },
    content: {
      vehicle: {
        make,
        model,
        imageUrl,
        id: vehicleId,
        trim,
        year,
        vehicleInventoryType,
        stockNumber
      },
      openVerifications: { hasOpenVerifications, openVerificationsCount }
    }
  } = feedItem;

  const customAttributes = {
    description: 'Order placed cta',
    vehicleId
  };

  const nativePayload = {
    make,
    model,
    trim,
    year,
    vehicleImage: imageUrl,
    inventoryType: vehicleInventoryType,
    stockNumber
  };

  const cardTitle = hasOpenVerifications
    ? "You're almost there!"
    : `Congrats on your new ${make}!`;

  return (
    <TrackWrapper
      event={formatEvent('Viewed - Home feed card', customAttributes)}
    >
      <Card data-qa="order-placed-card">
        <CardInfoSection>
          <CardHeader data-qa="order-placed-card-title">{cardTitle}</CardHeader>
          {!hasOpenVerifications && <AllTasksComplete />}

          {!isMobile && <OpdVehicleDetails feedItem={feedItem} />}

          {hasOpenVerifications && (
            <a
              href={callToActionLink}
              data-qa="order-placed-verifications-button"
              data-analytics-id="Home feed card"
              data-analytics-attributes={JSON.stringify(customAttributes)}
              data-native-included="false"
              onClick={event =>
                handleNativePurchaseOnClick(event, nativePayload)
              }
            >
              <RemainingTasks
                cta={callToActionTitle}
                hasOpenVerifications={hasOpenVerifications}
                openVerificationsCount={openVerificationsCount}
              />
            </a>
          )}
          {!isMobile && !hasOpenVerifications && (
            <CardButtonWhite
              href={callToActionLink}
              data-analytics-id="Home feed card"
              data-analytics-attributes={JSON.stringify(customAttributes)}
              data-native-included="false"
              onClick={event =>
                handleNativePurchaseOnClick(event, nativePayload)
              }
            >
              {callToActionTitle}
            </CardButtonWhite>
          )}
        </CardInfoSection>

        <CardImageSection>
          <a
            href={callToActionLink}
            data-qa="purchase-in-progress-image-container"
            data-analytics-id="Home feed card"
            data-analytics-attributes={JSON.stringify(customAttributes)}
            data-native-included="false"
            onClick={event => handleNativePurchaseOnClick(event, nativePayload)}
          >
            <picture>
              <source
                srcSet={imgCropUrl(imageUrl, false)}
                media="(min-width=768px)"
              />
              <VehicleImage
                data-qa="order-placed-card-vehicle-image"
                alt={`${make} ${model}`}
                src={imgCropUrl(imageUrl, true)}
                onError={defaultImageHandler}
              />
            </picture>
          </a>
          {isMobile && <OpdVehicleDetails feedItem={feedItem} />}
          {isMobile && !hasOpenVerifications && (
            <CardButtonWhite
              href={callToActionLink}
              data-analytics-id="Home feed card"
              data-analytics-attributes={JSON.stringify(customAttributes)}
              data-native-included="false"
              onClick={event =>
                handleNativePurchaseOnClick(event, nativePayload)
              }
            >
              {callToActionTitle}
            </CardButtonWhite>
          )}
        </CardImageSection>
      </Card>
    </TrackWrapper>
  );
}
